import React, { useCallback, useMemo, useRef, useEffect } from "react";
import { useHistory } from "react-router";
import { compact } from "lodash";

import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import * as yup from "yup";

import { TalentsIcon } from "../components/icons";

import useInsertUser from "../hooks/useInsertUser";
import useTranslations from "../hooks/useTranslations";

import useViewer from "../hooks/useViewer";

// in case we are updating ourselves
import { GET_USERS } from "../graphql/hooks/queries/useGetUsers";

import {
  FormEntry,
  Button,
  ScreenHeader,
  Container,
  Radio,
  Input,
} from "../components/wombat-ui";
import useAddNotification from "../hooks/useAddNotification";
import { GET_VIEWER } from "../graphql/hooks/queries/useGetViewer";

type UserInput = {
  id: string;
  name: string;
  email: string;
  locale: string;
  role: string;
  avatar: {
    filename: string;
    data: string;
  };
};

const LOCALE_OPTIONS = [
  { value: "fr-FR", label: "Francais" },
  { value: "en-US", label: "English" },
];

type UserFormProps = {
  defaultValues?: UserInput;
};

const UserFormScreen: React.FC<UserFormProps> = ({ defaultValues = {} }) => {
  const {
    company: { id: companyId, locale },
    isCapableOfInsertingAdmins,
    isCapableOfInsertingManagers,
    isCapableOfInsertingUsers,
  } = useViewer();

  const roles = compact([
    isCapableOfInsertingUsers && "user",
    isCapableOfInsertingManagers && "manager",
    isCapableOfInsertingAdmins && "administrator",
  ]);

  const [addNotification] = useAddNotification();
  const t = useTranslations();

  const UserFormSchema = useMemo(
    () =>
      yup
        .object()
        .shape({
          name: yup.string().required(t("forms.errors.required") as string),
          email: yup
            .string()
            .email(t("forms.errors.email") as string)
            .required(t("forms.errors.required") as string),
          locale: yup.string(),
          role: yup.string(),
        })
        .required(),
    [t]
  );

  const {
    register,
    setValue,
    handleSubmit,
    errors,
    control,
  } = useForm<UserInput>({
    resolver: yupResolver(UserFormSchema),
    defaultValues: { ...defaultValues, locale },
  });

  const role = useWatch({
    name: `role`,
    control,
    defaultValue: defaultValues.role,
  });

  const history = useHistory();
  const [insertUser, { loading }] = useInsertUser({
    refetchQueries: [{ query: GET_USERS }],
  });
  const onSubmit = useCallback(
    async ({ name, email, locale, avatar }) => {
      await insertUser({
        variables: {
          input: {
            id: defaultValues.id,
            name,
            email,
            locale,
            role,
            avatar: avatar.filename !== "" ? avatar.data : null,
            companyId,
          },
        },
        refetchQueries: [
          { query: GET_VIEWER },
          { query: GET_USERS, variables: { page: 1, per: 30 } },
        ],
        awaitRefetchQueries: true,
      });
      history.push(`/users`);
      addNotification({
        variables: {
          title: t("userProfile.notification.success") as string,
          status: "success",
        },
      });
    },
    [defaultValues.id, role]
  );

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
  }, [errors]);

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
    setValue(`avatar.filename`, fileRef.current?.filename);
    setValue(`avatar.data`, fileRef.current?.data);
  }, []);

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
  }, [errors]);

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
    setValue(`avatar.filename`, fileRef.current?.filename);
    setValue(`avatar.data`, fileRef.current?.data);
  });

  const fileRef = useRef<{ filename: string; data: string }>(null);

  const avatarData = useWatch({
    control,
    name: `avatar.data`,
    defaultValue: "",
  });

  const handleAvatarChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const reader = new FileReader();
      const file = e.target?.files?.[0];
      reader.readAsDataURL(file);

      reader.onload = () => {
        setValue(`avatar.filename`, file.name);
        setValue(`avatar.data`, reader.result);
        fileRef.current = {
          filename: file.name,
          data: reader.result as string,
        };
      };
    },
    []
  );

  return (
    <>
      <ScreenHeader
        icon={TalentsIcon}
        title={
          defaultValues.name ? defaultValues.name : (t("talents.new") as string)
        }
        actions={[
          <Button
            key="submit"
            type="submit"
            variant="primary"
            form="addUserForm"
            disabled={loading}
          >
            {t("forms.actions.send")}
          </Button>,
        ]}
      />
      <Container>
        <form
          className="flex flex-col space-y-8"
          id="addUserForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div>
            <label className="block mt-6 text-sm font-medium leading-5 text-gray-700">
              {t("users.avatar")}
            </label>
            <div className="flex items-center mt-2">
              {avatarData !== "" || defaultValues?.avatar?.data ? (
                <img
                  className="w-12 h-12 rounded-full"
                  src={
                    avatarData !== "" ? avatarData : defaultValues?.avatar?.data
                  }
                  alt={`User avatar`}
                />
              ) : (
                <span className="w-12 h-12 overflow-hidden bg-gray-100 rounded-full">
                  <svg
                    className="w-full h-full text-gray-300"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
              )}
              <span className="relative ml-5 overflow-hidden rounded-md shadow-sm">
                <button
                  type="button"
                  className="px-3 py-2 text-sm font-medium leading-4 text-gray-700 transition duration-150 ease-in-out border border-gray-300 rounded-md hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800"
                >
                  {t("forms.actions.change")}
                </button>
                <input
                  className="absolute top-0 right-0 block py-2 opacity-0 cursor-pointer"
                  type="file"
                  onChange={handleAvatarChange}
                  accept="image/png, image/jpeg, image/jpg"
                  name="avatar"
                />
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 col-span-4 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                id="name"
                label={t("users.name") as string}
                error={errors?.name}
                name={`name`}
                defaultValue={defaultValues.name}
                ref={register({ required: true })}
              />
            </div>
            <div className="sm:col-span-4">
              <Input
                id="email"
                label={t("users.email") as string}
                error={errors?.email}
                name={`email`}
                defaultValue={defaultValues.email}
                ref={register({ required: true })}
              />
            </div>
          </div>

          <FormEntry title={t("users.locale")}>
            {LOCALE_OPTIONS.map(({ label, value }) => (
              <Radio
                key={value}
                id={`locale-${value}`}
                label={label}
                name="locale"
                value={value}
                ref={register}
              />
            ))}
          </FormEntry>

          {roles.length > 1 && (
            <FormEntry title={t("users.roles.role")}>
              <div className="-space-y-px bg-white rounded-md">
                {roles.map((roleId, index) => (
                  <div
                    className={`
                          relative flex p-4 border
                          ${index === 0 ? "rounded-tl-md rounded-tr-md" : ""}
                          ${
                            index === roles.length - 1
                              ? "rounded-bl-md rounded-br-md"
                              : ""
                          }
                          ${
                            roleId === role
                              ? "bg-primary-100 border-primary-200 z-10"
                              : ""
                          }
                        `}
                    key={roleId}
                  >
                    <div className="flex items-center h-5">
                      <input
                        id={roleId}
                        data-test-id={roleId}
                        name="role"
                        type="radio"
                        className="w-4 h-4 border-gray-300 cursor-pointer text-primary-600 focus:ring-primary-500"
                        value={roleId}
                        ref={register}
                      />
                    </div>
                    <label
                      htmlFor={roleId}
                      className="flex flex-col ml-3 cursor-pointer"
                    >
                      <span className="block text-sm font-medium">
                        {t(`users.roles.${roleId}.label`)}
                      </span>
                      <span className="block text-sm">
                        {t(`users.roles.${roleId}.description`)}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </FormEntry>
          )}
        </form>
      </Container>
    </>
  );
};

export default UserFormScreen;
